import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useBalance } from '../hooks/useBalance';
import { Shield, Search, BookOpen, Volume2, VolumeX } from 'lucide-react';
import Logo from './Logo';
import NotificationsDropdown from './NotificationsDropdown';
import '../styles/navbar-gaming.css';
import { cardsApi } from '../api/cards';
import { formatD10S } from '../utils/formatters';
import { useLanguage } from '../i18n/LanguageContext';
import { useSound } from '../context/SoundContext';
import { useMusic } from '../context/MusicContext';

const Navbar = () => {
  const location = useLocation();
  const searchContainerRef = useRef(null);
  const { t } = useLanguage();
  const { playSound } = useSound();
  const { enabled: musicEnabled, setEnabled: setMusicEnabled } = useMusic();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());
  // Initialize user state only once, using a function to avoid re-initialization
  const [user, setUser] = useState(() => {
    try {
      const currentUser = authApi.getCurrentUser();
      // Preserve admin status on initialization
      if (currentUser?.isAdmin || currentUser?.adminRole) {
        const preservedUser = {
          ...currentUser,
          isAdmin: true,
          adminRole: currentUser.adminRole || 'ADMIN'
        };
        // Only update localStorage if user actually changed
        const storedUser = localStorage.getItem('user');
        if (!storedUser || JSON.parse(storedUser).id !== preservedUser.id) {
          localStorage.setItem('user', JSON.stringify(preservedUser));
        }
        return preservedUser;
      }
      return currentUser;
    } catch (error) {
      return null;
    }
  });
  
  // Usa l'hook centralizzato per il balance
  const balance = useBalance();

  useEffect(() => {
    // Only listen for storage events from other tabs
    const handleStorageChange = () => {
      const currentUser = authApi.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    setShowResults(true);
    try {
      const response = await cardsApi.searchPlayerCards(trimmed, 5);
      if (response?.success) {
        setSearchResults(response.results || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchError(error.message || t('navbar.errorSearch'));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const togglePlayerDetails = (playerId) => {
    setExpandedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  return (
    <div className="gaming-navbar-wrapper">
      <nav className="gaming-navbar">
        <div className="gaming-navbar-container">
        {/* Left: Logo */}
        <Link to="/" className="gaming-navbar-brand" data-tutorial="logo" onClick={() => playSound('click')}>
          <Logo size="nav" showText={false} />
        </Link>

        {/* Center: Player Cards Search */}
        <div className="gaming-navbar-center" ref={searchContainerRef}>
          <form className="gaming-navbar-search" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('navbar.searchPlaceholder')}
              className="gaming-navbar-search-input"
            />
            <button type="submit" className="gaming-navbar-search-button" aria-label={t('navbar.searchAriaLabel')} onClick={() => playSound('click')}>
              <Search size={18} />
            </button>
          </form>

          {showResults && (
            <div className="gaming-navbar-search-results">
              {searchLoading && (
                <div className="gaming-search-status">{t('navbar.searching')}</div>
              )}
              {!searchLoading && searchError && (
                <div className="gaming-search-status gaming-search-error">{searchError}</div>
              )}
              {!searchLoading && !searchError && searchResults.length === 0 && (
                <div className="gaming-search-status">{t('navbar.noResults')}</div>
              )}
              {!searchLoading && !searchError && searchResults.length > 0 && (
                <div className="gaming-search-results-list">
                  {searchResults.map((result) => {
                    const playerId = result.player?.id;
                    const isExpanded = expandedPlayers.has(playerId);
                    return (
                    <div
                      key={playerId}
                      className="gaming-search-player"
                      onClick={() => togglePlayerDetails(playerId)}
                      role="button"
                      aria-expanded={isExpanded}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          togglePlayerDetails(playerId);
                        }
                      }}
                    >
                      <div className="gaming-search-player-header">
                        <div className="gaming-search-player-name">{result.player?.full_name}</div>
                        <div className="gaming-search-player-meta">
                          {result.player?.team || t('common.notAvailable')} • {result.player?.position || t('common.notAvailable')} • {result.player?.nationality || t('common.notAvailable')}
                        </div>
                      </div>
                      <div className="gaming-search-player-total">
                        {t('navbar.totalCards')}: {result.totalCards || 0}
                      </div>
                      {isExpanded && (
                        <div className="gaming-search-rarities">
                          {Object.entries(result.totalsByRarity || {}).map(([rarity, count]) => (
                            <div key={`${playerId}-${rarity}`} className="gaming-search-rarity">
                              <div className="gaming-search-rarity-title">
                                {rarity}: {count}
                              </div>
                              <div className="gaming-search-rarity-owners">
                                {(result.ownersByRarity?.[rarity] || []).map((owner, index) => (
                                  <div key={`${playerId}-${rarity}-${owner.ownerId || 'company'}-${index}`} className="gaming-search-owner">
                                    {(owner.ownerId
                                      ? (owner.galleryPublic === false
                                        ? '#####'
                                        : (owner.username || owner.email || `User ${owner.ownerId}`))
                                      : t('navbar.company'))}: {owner.count}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )})}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: User Info */}
        <div className="gaming-navbar-right">
          {user ? (
            <>
              {/* Rules - left of balance */}
              <Link
                to="/rules"
                target="_blank"
                rel="noopener noreferrer"
                className="gaming-rules-icon"
                title={t('navbar.rules')}
                onClick={() => playSound('click')}
                aria-label={t('navbar.rules')}
              >
                <BookOpen className="w-6 h-6 text-[#4169E1] hover:text-[#1e40af] transition-colors" />
              </Link>

              {/* Currency - Blue Coin + White Box + Red Add Button */}
              <div className="gaming-currency" data-tutorial="nav-deposit">
                <span className="gaming-currency-icon">💰</span>
                <span className="gaming-currency-amount">{balance !== null ? formatD10S(balance) : '0.00'}</span>
                <Link to="/deposit" className="gaming-currency-add" onClick={() => playSound('click')}>+</Link>
              </div>

              {/* Notifications */}
              <NotificationsDropdown />

              {/* Admin Icon - Always show if user is admin or super admin */}
              {(user?.isAdmin || user?.adminRole === 'ADMIN' || user?.adminRole === 'SUPER_ADMIN') && (
                <Link to="/admin" className="gaming-admin-icon" title={t('navbar.adminPanel')} onClick={() => playSound('click')}>
                  <Shield className="w-6 h-6 text-[#4169E1] hover:text-[#1e40af] transition-colors" />
                </Link>
              )}

              {/* Background music on/off — stesso stile icone admin/regole */}
              <button
                type="button"
                className={`gaming-navbar-audio-toggle${musicEnabled ? '' : ' gaming-navbar-audio-toggle--off'}`}
                title={musicEnabled ? t('navbar.musicMute') : t('navbar.musicUnmute')}
                aria-label={musicEnabled ? t('navbar.musicMute') : t('navbar.musicUnmute')}
                aria-pressed={musicEnabled}
                onClick={() => {
                  playSound('click');
                  setMusicEnabled(!musicEnabled);
                }}
              >
                {musicEnabled ? (
                  <Volume2 className="w-6 h-6 text-[#4169E1] hover:text-[#1e40af] transition-colors" />
                ) : (
                  <VolumeX className="w-6 h-6 text-gray-400 hover:text-gray-300 transition-colors" />
                )}
              </button>

              {/* User Avatar */}
              <Link to="/profile" className="gaming-user-avatar" data-tutorial="nav-profile" onClick={() => playSound('click')}>
                <div className="gaming-avatar-circle">
                  {user.username?.[0]?.toUpperCase() || 'U'}
                </div>
              </Link>
            </>
          ) : (
            <div className="gaming-navbar-auth">
              <Link to="/login" className="gaming-btn-nav" onClick={() => playSound('click')}>{t('navbar.signIn')}</Link>
              <Link to="/register" className="gaming-btn-nav gaming-btn-nav-primary" onClick={() => playSound('click')}>{t('navbar.register')}</Link>
            </div>
          )}
        </div>
      </div>
    </nav>
    </div>
  );
};

export default Navbar;
